import { useState } from 'react'
import { ConsentItem, CONSENT_TEXT, CONSENT_VERSION } from '@cs/shared'
import { api, setToken } from '../api.js'
import { describeError } from '../App.js'

/**
 * Two ways in: an invite code (the normal path — the inviter sends a link), or an
 * existing token pasted from another device. Both end with a token in storage.
 */
export function Join({ onJoined, code: given }: { onJoined: () => void; code?: string }) {
  // `given` is passed when an invite link was opened by somebody who is already
  // signed in but has not accepted the current rules — the code is known, only the
  // consent is missing, so they should not have to retype it.
  const initialCode = given ?? new URLSearchParams(location.search).get('code') ?? ''
  const [code, setCode] = useState(initialCode)
  const [name, setName] = useState('')
  const [token, setTokenInput] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [accepted, setAccepted] = useState<ConsentItem[]>([])

  const allAccepted = ConsentItem.options.every((i) => accepted.includes(i))
  const toggle = (item: ConsentItem) =>
    setAccepted((prev) => (prev.includes(item) ? prev.filter((x) => x !== item) : [...prev, item]))

  const redeem = async () => {
    setBusy(true)
    setError(null)
    try {
      const result = await api.redeem(code.trim(), name.trim() || undefined, accepted)
      // Null when the invite was redeemed with a token already in hand: the project
      // was added to that identity, so there is nothing new to store.
      if (result.token) setToken(result.token)
      onJoined()
    } catch (err) {
      setError(describeError(err))
    } finally {
      setBusy(false)
    }
  }

  const useExisting = async () => {
    setBusy(true)
    setError(null)
    setToken(token.trim())
    try {
      await api.me()
      onJoined()
    } catch (err) {
      setError(describeError(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="center">
      <div className="card">
        <h2>CrossSession に参加</h2>
        {error && <div className="error">{error}</div>}

        <h3>招待コードで参加</h3>
        <label className="muted" htmlFor="code">
          招待コード
        </label>
        <input
          id="code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="K7QM-2F9X-PD4T"
          autoComplete="off"
        />
        <div style={{ height: 8 }} />
        <label className="muted" htmlFor="name">
          表示名 (空欄なら招待時の名前)
        </label>
        <input id="name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" />
        <div style={{ marginTop: 16 }}>
          <h3>参加の条件</h3>
          <p className="muted" style={{ marginTop: 0 }}>
            このシステムは<strong>別会社との共有の場</strong>です。以下すべてに同意してください。
          </p>
          {ConsentItem.options.map((item) => (
            <label
              key={item}
              className="row"
              style={{ gap: 8, alignItems: 'flex-start', marginBottom: 8, fontSize: 13 }}
            >
              <input
                type="checkbox"
                checked={accepted.includes(item)}
                onChange={() => toggle(item)}
                style={{ width: 'auto', marginTop: 3 }}
              />
              <span>{CONSENT_TEXT[item]}</span>
            </label>
          ))}
          <div className="muted" style={{ fontSize: 12 }}>
            版: {CONSENT_VERSION}（同意した内容と日時が記録に残ります）
          </div>
        </div>

        <div className="row" style={{ marginTop: 10, justifyContent: 'flex-end' }}>
          <button
            className="primary"
            disabled={busy || !code.trim() || !allAccepted}
            onClick={redeem}
          >
            同意して参加する
          </button>
        </div>
      </div>

      <div className="card">
        <h3>既存のトークンで入る</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          別の端末で発行済みのトークン (cs_live_…) を貼り付けます。
        </p>
        <input
          value={token}
          onChange={(e) => setTokenInput(e.target.value)}
          placeholder="cs_live_..."
          autoComplete="off"
        />
        <div className="row" style={{ marginTop: 10, justifyContent: 'flex-end' }}>
          <button disabled={busy || !token.trim()} onClick={useExisting}>
            このトークンを使う
          </button>
        </div>
      </div>

      {/* The invite link is where a new member actually lands, so the tool they need
          is offered here rather than sent separately. One file, no build step. */}
      <div className="card">
        <h3>Claude Code から使う場合</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          自分の Claude Code をこの場につなぐには、ブリッジを 1 ファイル置くだけです。
          インストールもビルドも要りません。
        </p>
        <div className="row" style={{ marginTop: 10, gap: 10, flexWrap: 'wrap' }}>
          <a href="/download/crosssession.mjs" download>
            crosssession.mjs をダウンロード
          </a>
          <a className="muted" href="/download/crosssession.mjs.sha256">
            SHA-256
          </a>
        </div>
        <p className="muted" style={{ marginBottom: 0 }}>
          手順は配布された「セットアップ手順」(setup-member.md) をご覧ください。
          Windows・Mac どちらも書いてあります。
        </p>
      </div>
    </div>
  )
}
